/**
 * Tenant-aware public site origins for customer-facing links (/r/, /pay, quotes, WhatsApp shares).
 */

import { DEFAULT_SITE_ORIGIN, isLoopbackPublicOrigin } from './absoluteSiteUrl';
import { fetchOrgPublicHostContext } from './companySettingsExport';
import { isSaasPlatform } from './companyBranding';

export { isLoopbackPublicOrigin } from './absoluteSiteUrl';

const PRODUCTION_APP_URL = DEFAULT_SITE_ORIGIN;

export type TenantPublicOriginInput = {
  customDomain?: string | null;
};

export type TenantPublicOriginOptions = {
  organizationId?: string | null;
  customDomain?: string | null;
  baseOrigin?: string;
};

// `import.meta.env` is `undefined` under a Node serverless runtime, so a bare
// `import.meta.env.VITE_APP_URL` access throws and crashes the function
// (FUNCTION_INVOCATION_FAILED). Resolve it safely for both Vite and Node.
function viteAppUrl(): string {
  try {
    return typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_URL
      ? String(import.meta.env.VITE_APP_URL).trim()
      : '';
  } catch {
    return '';
  }
}

/** Public origin fallback when no tenant custom domain (never localhost). */
export function getDefaultTenantPublicOrigin(): string {
  const fromEnv = viteAppUrl().replace(/\/+$/, '');
  if (fromEnv && !isLoopbackPublicOrigin(fromEnv)) {
    return fromEnv;
  }
  if (isSaasPlatform()) {
    return 'https://serviceapp.ai';
  }
  return PRODUCTION_APP_URL;
}

export function normalizeCustomDomainHost(domain: string): string {
  return domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
}

/** Public origin for customer links — tenant custom domain, then app URL, then T&B default. */
export function buildTenantPublicOrigin(input?: TenantPublicOriginInput): string {
  const custom = input?.customDomain?.trim();
  if (custom) {
    return `https://${normalizeCustomDomainHost(custom)}`;
  }
  return getDefaultTenantPublicOrigin();
}

/** Branded `/r/...` URL that rewrites to Supabase public storage (completion reports). */
export function buildPublicStorageRewriteUrl(storagePath: string, input?: TenantPublicOriginInput): string {
  const origin = buildTenantPublicOrigin(input);
  const encoded = storagePath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${origin}/r/${encoded}`;
}

let runtimeTenantPublicOrigin: string | null = null;

/** Set tenant public origin for sync helpers (TenantBrandProvider on SaaS workspaces). */
export function setRuntimeTenantPublicOrigin(origin: string | null): void {
  runtimeTenantPublicOrigin = origin?.trim().replace(/\/+$/, '') || null;
}

/** Resolved tenant public origin when async fetch completed; falls back to sync resolver. */
export function getRuntimeTenantPublicOrigin(): string {
  return runtimeTenantPublicOrigin || resolveTenantPublicOriginSync();
}

/** Sync tenant public origin — custom domain, current window origin, or platform default. */
export function resolveTenantPublicOriginSync(options?: TenantPublicOriginOptions): string {
  if (options?.baseOrigin?.trim()) {
    const base = options.baseOrigin.trim().replace(/\/+$/, '');
    if (!isLoopbackPublicOrigin(base)) return base;
  }
  const custom = options?.customDomain?.trim();
  if (custom) {
    return buildTenantPublicOrigin({ customDomain: custom });
  }
  if (typeof window !== 'undefined') {
    const origin = window.location.origin.replace(/\/+$/, '');
    if (!isLoopbackPublicOrigin(origin)) {
      return origin;
    }
  }
  return buildTenantPublicOrigin();
}

/** Async tenant public origin — loads org custom domain when `organizationId` is set. */
export async function resolveTenantPublicOrigin(options?: TenantPublicOriginOptions): Promise<string> {
  if (options?.baseOrigin?.trim()) {
    const base = options.baseOrigin.trim().replace(/\/+$/, '');
    if (!isLoopbackPublicOrigin(base)) return base;
  }
  if (typeof window !== 'undefined' && !options?.organizationId && !options?.customDomain) {
    const origin = window.location.origin.replace(/\/+$/, '');
    if (!isLoopbackPublicOrigin(origin)) {
      return origin;
    }
  }
  let customDomain = options?.customDomain?.trim() || null;
  if (!customDomain && options?.organizationId) {
    const hostCtx = await fetchOrgPublicHostContext(options.organizationId);
    customDomain = hostCtx.customDomain;
  }
  if (customDomain) {
    return buildTenantPublicOrigin({ customDomain });
  }
  return getDefaultTenantPublicOrigin();
}
